import {
    AnimationComponent,
    Component,
    Object3D,
    Animation,
    Skin,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {setComponentsActive} from '../utils/activate-children.js';
import {clamp, lerpVec3} from '../utils/lerp.js';
import {vec3} from 'gl-matrix';

const tempVec3 = vec3.create();

/**
 * Controls the animation of the killer.
 *
 * There are 3 animations that need to run simultaneously:
 * For example:
 * - gen_origin => gen_origin-walk2idle_R
 * - gen_root => gen_root-walk2idle_R
 * - gem_spine01 => Amature-walk2idle_R
 *
 * All animations follow a similar naming convention.
 */
export class KillerAnimationController extends Component {
    static TypeName = 'killer-animation-controller';

    @property.object({required: true})
    gen_origin!: Object3D;

    @property.object({required: true})
    gen_root!: Object3D;

    @property.object({required: true})
    gen_spine01!: Object3D;

    @property.skin({required: true})
    skin!: Skin;

    @property.skin({required: true})
    skinSearchLeft!: Skin;

    @property.skin({required: true})
    skinWalk!: Skin;

    @property.object({required: true})
    idleMeshObject!: Object3D;

    @property.object({required: true})
    searchMeshObject!: Object3D;

    @property.object({required: true})
    walkTargetLocation!: Object3D;

    @property.animation({required: true})
    gen_origin_idle!: Animation;
    @property.animation({required: true})
    gen_root_idle!: Animation;
    @property.animation({required: true})
    armature_idle!: Animation;

    @property.animation({required: true})
    gen_origin_searchLeft!: Animation;
    @property.animation({required: true})
    gen_root_searchLeft!: Animation;
    @property.animation({required: true})
    armature_searchLeft!: Animation;

    @property.animation({required: true})
    gen_origin_walk!: Animation;
    @property.animation({required: true})
    gen_root_walk!: Animation;
    @property.animation({required: true})
    armature_walk!: Animation;

    private gen_originAnimation!: AnimationComponent;
    private gen_rootAnimation!: AnimationComponent;
    private armatureAnimation!: AnimationComponent;
    private walking = false;
    private killerStartPos = vec3.create();
    private killerTargetPos = vec3.create();

    private lerpTime = 0;

    start() {
        const tempOriginAnimation = this.gen_origin.getComponent(AnimationComponent);
        if (!tempOriginAnimation) {
            throw new Error(
                `killer-animation-controller(${this.object.name}): gen_origin does not have a OriginAnimation`
            );
        }
        this.gen_originAnimation = tempOriginAnimation;

        const tempRootAnimation = this.gen_root.getComponent(AnimationComponent);
        if (!tempRootAnimation) {
            throw new Error(
                `killer-animation-controller(${this.object.name}): gen_rootAnimation does not have a RootAnimation`
            );
        }
        this.gen_rootAnimation = tempRootAnimation;

        const tempArmatureAnimation = this.gen_spine01.getComponent(AnimationComponent);
        if (!tempArmatureAnimation) {
            throw new Error(
                `killer-animation-controller(${this.object.name}): gen_spine01 does not have a ArmatureAnimation`
            );
        }
        this.armatureAnimation = tempArmatureAnimation;

        this.playIdle();
    }

    playIdle() {
        this.gen_originAnimation.animation = this.gen_origin_idle;
        this.gen_rootAnimation.animation = this.gen_root_idle;
        this.armatureAnimation.animation = this.armature_idle;

        this.gen_originAnimation.animation?.retarget(this.skin);
        this.gen_rootAnimation.animation?.retarget(this.skin);
        this.armatureAnimation.animation?.retarget(this.skin);

        this.gen_originAnimation.playCount = 0;
        this.gen_rootAnimation.playCount = 0;
        this.armatureAnimation.playCount = 0;

        this.gen_originAnimation.play();
        this.gen_rootAnimation.play();
        this.armatureAnimation.play();
    }

    playSearchLeft() {
        setComponentsActive(this.idleMeshObject, false);
        this.gen_originAnimation.animation = this.gen_origin_searchLeft;
        this.gen_rootAnimation.animation = this.gen_root_searchLeft;
        this.armatureAnimation.animation = this.armature_searchLeft;

        this.gen_originAnimation.playCount = 0;
        this.gen_rootAnimation.playCount = 0;
        this.armatureAnimation.playCount = 0;

        this.gen_originAnimation.play();
        this.gen_rootAnimation.play();
        this.armatureAnimation.play();
        this.gen_rootAnimation.object.setDirty();
    }

    playWalk() {
        this.killerStartPos = this.object.getPositionWorld();
        this.killerTargetPos = this.walkTargetLocation.getPositionWorld();
        this.object.setRotationWorld([0.0236018, 0.499837, 0.038614, 0.864936]);
        this.walking = true;
        setComponentsActive(this.searchMeshObject, false);
        this.gen_originAnimation.animation = this.gen_origin_walk;
        this.gen_rootAnimation.animation = this.gen_root_walk;
        this.armatureAnimation.animation = this.armature_walk;

        this.gen_originAnimation.playCount = 0;
        this.gen_originAnimation.speed = 3;
        this.gen_rootAnimation.playCount = 0;
        this.gen_rootAnimation.speed = 3;
        this.armatureAnimation.playCount = 0;
        this.armatureAnimation.speed = 3;

        this.gen_originAnimation.play();
        this.gen_rootAnimation.play();
        this.armatureAnimation.play();
    }

    update(dt: number) {
        if (this.walking) {
            lerpVec3(tempVec3, this.killerStartPos, this.killerTargetPos, this.lerpTime);
            this.lerpTime += dt;
            clamp(this.lerpTime, 0, 1);
            this.object.setPositionWorld(tempVec3);
        }
    }
}
